import React, { useEffect, useRef, useState } from 'react';

const PostToTopicAnimation = () => {
  const [scrollPosition, setScrollPosition] = useState(0); // 0..100
  const [direction, setDirection] = useState(1); // 1 -> right, -1 -> left
  const directionRef = useRef(1);

  useEffect(() => {
    directionRef.current = direction;
  }, [direction]);

  useEffect(() => {
    // smoother timing and stable interval using refs
    const STEP = 1; // percent increment per tick
    const TICK = 50; // ms

    const id = setInterval(() => {
      setScrollPosition(prev => {
        let next = prev + directionRef.current * STEP;
        if (next >= 100) {
          next = 100;
          directionRef.current = -1;
          setDirection(-1);
        } else if (next <= 0) {
          next = 0;
          directionRef.current = 1;
          setDirection(1);
        }
        return Math.round(next * 100) / 100;
      });
    }, TICK);

    return () => clearInterval(id);
  }, []);

  const posts = [
    {
      name: "Karan Goel",
      title: "Founder / CEO @ Cartesia",
      text: "We've raised $100M from Kleiner Perkins, Index Ventures, Lightspeed, & NVIDIA. Today we're introducing...",
      avatar: "/assets/profile-display-photo-8.jpeg"
    },
    {
      name: "Angela Strange",
      title: "General Partner, Andreessen Horowitz",
      text: "Fintech isn’t dead.  It’s at a new beginning 🚀 Tech in financial services adapts/ creates large opportunities in every product cycle...",
      avatar: "/assets/profile-display-photo-9.jpeg"
    },
    {
      name: "Cursor",
      title: "The best way to code with AI",
      text: "We're proud to work with NVIDIA to support their nearly 40,000 engineers...",
      avatar: "/assets/profile-display-photo-10.jpeg"
    }
  ];

  const topics = [
    {
      title: "AI & Machine Learning",
      description: "Advanced artificial intelligence and ML implementations are dominating discussions"
    },
    {
      title: "Product Launches",
      description: "New product releases and feature announcements trending across the platform"
    },
    {
      title: "Analytics & Data",
      description: "Data-driven insights and analytics tools are capturing audience attention"
    }
  ];

  // dividerX is computed so the right layer shows scrollPosition% of the width.
  const dividerX = Math.max(0, Math.min(100, 100 - scrollPosition));
  const rightClip = `polygon(${dividerX}% 0, 100% 0, 100% 100%, ${dividerX}% 100%)`;

  return (
    <div className="relative w-full h-64 md:h-80 bg-gradient-to-br from-white via-slate-50 to-slate-50 rounded-xl md:rounded-2xl overflow-hidden shadow-inner">
      {/* LEFT LAYER - Posts (bottom) */}
      <div className="absolute inset-0 z-10">
        <div className="flex h-full">
          <div className="w-full p-3 md:p-6 overflow-hidden">
            <h4 className="text-[10px] md:text-sm font-bold text-gray-700 mb-2 md:mb-4">Trending Posts</h4>
            <div className="h-full overflow-hidden pr-1 md:pr-2 space-y-2 md:space-y-3">
              {posts.map((post, index) => (
                <article
                  key={index}
                  className="rounded-md md:rounded-lg p-2 md:p-4 backdrop-blur-sm bg-white/60 border border-white/30"
                >
                  <div className="flex items-start gap-1.5 md:gap-3 mb-1 md:mb-2">
                    <div className="w-5 h-5 md:w-9 md:h-9 rounded-full flex-shrink-0" ><img alt={post.name} src={post.avatar} className="w-full h-full object-cover rounded-full" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[8px] md:text-xs font-semibold text-gray-900 truncate">{post.name}</p>
                      <p className="text-[7px] md:text-xs text-gray-500 truncate">{post.title}</p>
                    </div>
                  </div>
                  <p className="text-[8px] md:text-xs text-gray-700 leading-snug line-clamp-2">{post.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT LAYER - Topics (top) clipped to show only the right portion.
          KEY FIXES:
          - give the entire clipped region a solid white backing (absolute inset-0 bg-white)
          - remove vertical gaps between cards (no space-y); use border-top separators instead */}
      <div
        className="absolute inset-0 z-30 pointer-events-none"
        style={{
          clipPath: rightClip,
          transition: 'clip-path 240ms linear',
          willChange: 'clip-path'
        }}
      >
        {/* Full white backing so no underlying content can show through the gaps */}
        <div className="absolute inset-0 bg-white" />

        <div className="relative flex h-full">
          <div className="w-full p-0 overflow-hidden">
            {/* Use a single column with no gaps so cards stack seamlessly */}
            <div className="h-full overflow-hidden">
              <div className="p-3 md:p-6">
                <h4 className="text-[10px] md:text-sm font-bold text-gray-700 mb-0">Trending Topics</h4>
              </div>

              <div className="flex flex-col h-[calc(100%-40px)] md:h-[calc(100%-56px)]"> {/* allocate remaining height under the header */}
                {topics.map((topic, index) => (
                  <article
                    key={index}
                    className={`p-2 md:p-4 ${index === 0 ? '' : 'border-t border-gray-100'}`}
                    style={{ background: 'white' }} // ensure opaque
                  >
                    <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2">
                      <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full block flex-shrink-0" />
                      <h5 className="text-[9px] md:text-sm font-bold text-gray-900 truncate">{topic.title}</h5>
                    </div>
                    <p className="text-[8px] md:text-xs text-gray-500 line-clamp-2">{topic.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Divider / Slider */}
      <div
        className="absolute top-0 bottom-0 z-40"
        style={{
          left: `${dividerX}%`,
          transform: 'translateX(-50%)',
          transition: 'left 240ms linear',
          willChange: 'left'
        }}
      >
        <div className="w-0.5 h-full relative">
          {/* glowing gradient line */}
          <div
            className="absolute inset-0 rounded-sm"
            style={{
              width: 3,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(to bottom, rgba(59,130,246,0.95), rgba(139,92,246,0.9), rgba(34,197,94,0.9))',
              boxShadow: '0 4px 15px rgba(139,92,246,0.12), 0 0 20px rgba(59,130,246,0.06)',
              borderRadius: 4,
              filter: 'blur(0.3px)'
            }}
          />
          {/* center knob with arrow */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 md:w-10 md:h-10 rounded-full bg-white/95 flex items-center justify-center shadow"
            style={{
              transform: `translate(-50%, -50%) rotate(${direction === 1 ? 0 : 180}deg)`,
              transition: 'transform 240ms linear'
            }}
          >
            <svg
              className="w-3 h-3 md:w-5 md:h-5 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          {/* subtle halo */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              width: 36,
              height: 36,
              borderRadius: '999px',
              background: 'radial-gradient(closest-side, rgba(139,92,246,0.08), transparent)',
              pointerEvents: 'none'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PostToTopicAnimation;
